/* Greenleaf — the product media stage.
 *
 * Replaces gl-spin.js, which was a 360 viewer and nothing else. The stage now
 * switches between the GemIQ video, the 360 and the photographs, so one file
 * owns all three: switching is the whole point, and splitting it across two
 * scripts would mean two things deciding which view is showing.
 *
 * Four things here are load-bearing, each because of a real defect:
 *
 *  - **The spin loads ON DEMAND.** Its sheets are ~1.4MB each and there are up
 *    to five. Fetching them on page load, for the majority of visitors who never
 *    drag, is what would make the 1480px frames unaffordable. Nothing is
 *    requested until a customer asks to turn the piece.
 *  - **Draws to a CANVAS.** An earlier prototype swapped `display` on stacked
 *    <img> elements and read as choppy; one drawImage per rAF does not.
 *  - **The first sheet is enough to drag.** The rest stream in behind, so 72
 *    frames costs the customer no more waiting than 15 would.
 *  - **The video is paused when it is not showing.** A looping 12s clip
 *    decoding behind a photograph is battery and heat for nothing, and on a
 *    phone it is the difference between a warm device and a cool one.
 *
 * 10 Sep 2026 (design pass, Package 21) — Andrew: the 360 "should feel
 * instant and smooth, not laggy". Measured on the pink sapphire ring first:
 * six sheets, 480 KB each as the WebP the CDN already negotiates (795 KB as
 * the JPEG a bare curl gets), nothing requested until the first pointerdown,
 * so the first drag waited for sheet 0 before anything moved; 11px of mouse
 * per frame, so a full turn was 792px of travel; and the ring stopped dead
 * the instant the pointer lifted. Four changes, all here:
 *
 *  - **Sheet 0 is fetched when the stage scrolls into view**, idle-time,
 *    before anyone has asked to turn anything. Under strided packing (see
 *    locate()) sheet 0 alone is a complete coarse turn, so by the time a
 *    customer reaches the 360 there is already a whole ring to drag. The
 *    other five load on the first hover or pointerdown over the stage, or
 *    when the 360 view is chosen — not on page load, for the visitor who
 *    never turns anything. "On demand" above still holds for 5/6 of the bytes.
 *  - **A partly loaded spin turns coarsely instead of not at all.** See
 *    step() and nearestDrawable(): the old hold-at-the-edge rule, written for
 *    consecutive packing, refused every one-frame step under strided packing.
 *  - **STEP 11 → 6**: a full turn in about 430px of travel.
 *  - **Momentum.** Velocity is tracked over the last few pointer moves and
 *    the turn coasts after release, decaying over ~600ms, stepping frames as
 *    it slows. A ring that stops the instant a finger lifts reads as a
 *    slideshow; one that coasts reads as an object. None under
 *    prefers-reduced-motion.
 *  - **One slow quarter turn on arrival** (29 Sep 2026, replaced the old
 *    30°-and-back nudge). When the 360 view opens, once enough frames are in,
 *    the ring turns a quarter over ~1.8s, and a faint ring-and-arrow mark sits
 *    in the corner until the customer really turns it. That is the affordance
 *    the angle readout used to be, without a box on the photograph
 *    (Andrew's standing rule, Package 15). Off under reduced motion.
 *
 * The angle readout (`[data-stage-angle]`) is gone with Package 15, so the
 * `readout` plumbing that fed it is gone from here too.
 */
(function () {
  'use strict';

  /* MOUSE WORDS ON A TOUCH SCREEN. Both the visible hint under the stage and the
   * zoom button's accessible name said "click", on a product page most people
   * open on a phone. Read from `(hover:none)` rather than from the user agent or
   * from a touch-events test: a laptop with a touchscreen still has a pointer and
   * should still be told to click, and that is exactly what this query answers. */
  function tapOrClick(rest) {
    // "Tap" on a touchscreen OR on any screen 900px wide or less (Andrew,
    // 2 Oct 2026): a phone-sized window is read as a phone, whatever pointer it has.
    var touch = window.matchMedia && window.matchMedia('(hover:none), (max-width:900px)').matches;
    return (touch ? 'Tap ' : 'Click ') + rest;
  }

  function dragToTurn() {
    var touch = window.matchMedia && window.matchMedia('(hover:none)').matches;
    return touch ? 'Drag to turn' : 'Drag to turn, or use the arrow keys';
  }


  function FitVideo(v) {
    var view = v.closest('.stage__view');
    var TARGET = 0.66;      // of the stage's width: what the piece fills in the 360, measured 0.65-0.69
    var MAX_H = 0.82;       // never let a tall piece run out of the stage
    var MAX_ZOOM = 2.4;     // over the cover scale
    var union = null, tries = 0, edge = null, last = null;
    function bbox() {
      if (!v.videoWidth || !v.videoHeight) return null;
      var w = 160, h = Math.max(1, Math.round(w * v.videoHeight / v.videoWidth));
      var c = document.createElement('canvas'); c.width = w; c.height = h;
      var x = c.getContext('2d'), d;
      try { x.drawImage(v, 0, 0, w, h); d = x.getImageData(0, 0, w, h).data; } catch (e) { return null; }
      last = c;
      var x0 = w, x1 = -1, y0 = h, y1 = -1, i, j, k, l, sat;
      for (j = 0; j < h; j++) {
        for (i = 0; i < w; i++) {
          k = (j * w + i) * 4;
          l = (d[k] + d[k + 1] + d[k + 2]) / 3;
          sat = Math.max(d[k], d[k + 1], d[k + 2]) - Math.min(d[k], d[k + 1], d[k + 2]);
          if (l < 150 || sat > 40) {
            if (i < x0) x0 = i; if (i > x1) x1 = i; if (j < y0) y0 = j; if (j > y1) y1 = j;
          }
        }
      }
      // The backdrop at the frame's top and bottom edge, for the bands a wide
      // loop leaves when it is shown at less than cover.
      var t = 3 * 4 + 1 * w * 4, b2 = 3 * 4 + (h - 2) * w * 4;
      edge = { top: 'rgb(' + d[t] + ',' + d[t + 1] + ',' + d[t + 2] + ')',
               bottom: 'rgb(' + d[b2] + ',' + d[b2 + 1] + ',' + d[b2 + 2] + ')' };
      if (x1 < 0) return null;
      // A read that says the piece covers nearly the whole frame is a bad
      // frame (a fade, a black first frame), not a piece; it would pin the
      // fit at no zoom, so it is dropped rather than unioned in.
      if ((x1 + 1 - x0) / w > 0.9 || (y1 + 1 - y0) / h > 0.9) return null;
      return { x0: x0 / w, x1: (x1 + 1) / w, y0: y0 / h, y1: (y1 + 1) / h };
    }
    // The <video> is given an explicit box rather than a transform: a
    // translated element drags its own clipping box with it and shows the
    // stage behind, so a 16:9 loop shifted left left a white gap at the right.
    // Here the box IS the scaled video, positioned so the piece is centred,
    // and the stage's overflow clips whatever runs past its edge.
    function apply() {
      var b = union, r = view.getBoundingClientRect();
      if (!b || !r.width || !r.height || !v.videoWidth) return;
      var W = r.width, H = r.height, vw = v.videoWidth, vh = v.videoHeight;
      var kCover = Math.max(W / vw, H / vh), kFit = Math.min(W / vw, H / vh);
      var pw = (b.x1 - b.x0) * vw, ph = (b.y1 - b.y0) * vh;
      var k = Math.min(TARGET * W / pw, MAX_H * H / ph, MAX_ZOOM * kCover);
      k = Math.max(k, kFit);
      var w = vw * k, h = vh * k;
      var left = W / 2 - (b.x0 + b.x1) / 2 * w, top = H / 2 - (b.y0 + b.y1) / 2 * h;
      // Wider than the stage: slide, but never past an edge. Narrower: centre,
      // and paint the bands with the backdrop's own edge colours.
      if (w >= W) left = Math.min(0, Math.max(W - w, left)); else left = (W - w) / 2;
      if (h >= H) top = Math.min(0, Math.max(H - h, top)); else top = (H - h) / 2;
      v.style.width = w.toFixed(1) + 'px'; v.style.height = h.toFixed(1) + 'px';
      v.style.left = left.toFixed(1) + 'px'; v.style.top = top.toFixed(1) + 'px';
      if (last && h < H - 1) paintBands(top, h, W, H);
      v.setAttribute('data-fitted', '');
      v.setAttribute('data-fit-box', [b.x0, b.x1, b.y0, b.y1].map(function (n) { return n.toFixed(3); }).join(' ') + ' k=' + k.toFixed(3));
    }
    // The bands above and below a wide loop are the loop's own top and bottom
    // rows, stretched and blurred - the same fill the 360 uses - painted once
    // into a small canvas and set as the view's background. The backdrop does
    // not move during the turn, so a still of it behind the video is seamless.
    function paintBands(top, h, W, H) {
      var bw = 160, bh = Math.max(1, Math.round(bw * H / W)), sc = bw / W;
      var c = document.createElement('canvas'); c.width = bw; c.height = bh;
      var x = c.getContext('2d'), lw = last.width, lh = last.height;
      // One row, lightly blurred. A thicker strip on a 16:9 loop reaches the
      // set's horizon at the top and the reflection's edge at the bottom and
      // stretches those into a dark band; the outermost row is the plain wall
      // and the plain floor, which is what the band should continue.
      var strip = 1, pad = 16;
      if ('filter' in x) x.filter = 'blur(3px)';
      var ty = Math.round(top * sc), by = Math.round((top + h) * sc);
      x.drawImage(last, 0, 0, lw, strip, -pad, -pad, bw + pad * 2, ty + pad + 2);
      x.drawImage(last, 0, lh - strip, lw, strip, -pad, by - 2, bw + pad * 2, bh - by + pad + 2);
      if ('filter' in x) x.filter = 'none';
      try {
        view.style.backgroundImage = 'url(' + c.toDataURL('image/jpeg', 0.7) + ')';
        view.style.backgroundSize = '100% 100%';
      } catch (e) { view.style.background = 'linear-gradient(' + edge.top + ',' + edge.bottom + ')'; }
    }
    // Three samples, three seconds apart, across the twelve-second turn: a
    // bracelet is twice as wide side-on as end-on, and the box has to hold
    // the piece at every angle or the fit would breathe as it turns.
    function sample() {
      var b = bbox();
      if (b) {
        union = union ? { x0: Math.min(union.x0, b.x0), x1: Math.max(union.x1, b.x1),
                          y0: Math.min(union.y0, b.y0), y1: Math.max(union.y1, b.y1) } : b;
        apply();
      }
      if (++tries < 4) setTimeout(sample, 3000);
    }
    v.addEventListener('playing', function once() { v.removeEventListener('playing', once); sample(); });
    window.addEventListener('resize', apply);
  }

  function Stage(root) {
    var box = root.querySelector('[data-stage]');
    var nav = root.querySelector('[data-stage-nav]');
    var note = root.querySelector('[data-stage-note]');
    var bar = root.querySelector('[data-stage-load]');
    var fill = bar ? bar.querySelector('i') : null;
    var spinEl = root.querySelector('[data-spin]');
    if (!box) return;

    var spin = spinEl ? Spin(spinEl, bar, fill) : null;

    // ---- the video takes the 360's framing (19 Sep 2026) -------------------
    // The loops on the store are the vendor's full frame - square for most
    // pieces, 16:9 for a few - so the ring sat at 40-46% of the stage's width
    // in the video and 65-69% in the 360 and the photographs: the same piece,
    // two thirds the size, on one click. Nothing about the framing is stored
    // anywhere, so the page measures it: once the loop is playing it paints a
    // few frames of it to a small canvas, finds the piece (darker or more
    // saturated than the grey sweep), unions the boxes across the turn, and
    // scales and shifts the <video> so the piece fills the width the 360
    // gives it. The video is object-fit:cover underneath, so a 16:9 loop no
    // longer letterboxes either. Same-origin CDN, so the canvas is readable;
    // if it ever is not, the try/catch leaves the video exactly as it was.
    var vlist = box.querySelectorAll('video');
    // The spin videos (custom.spin_video) are framed by the pipeline that makes
    // them, so they are not re-measured; FitVideo is for the old GemIQ loops.
    for (var vi = 0; vi < vlist.length; vi++) {
      if (!vlist[vi].hasAttribute('data-spin-video')) FitVideo(vlist[vi]);
    }
    // Reduced motion: the video never starts itself. It shows its poster with
    // the browser's own play control, so motion is the customer's choice.
    var calm = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (calm) { for (var cv = 0; cv < vlist.length; cv++) vlist[cv].setAttribute('controls', ''); }

    function views() { return box.querySelectorAll('.stage__view'); }

    // DEFERRED PHOTOS (3 Oct 2026, speed pass 2). gl-stage-photo.liquid with
    // `defer: true` (the Worn view) ships its file in data-src/data-srcset so
    // nothing is fetched during the page load; the lead photo gets the
    // connection to itself. Woken in idle time once the page has loaded, or the
    // moment its view is pressed for, whichever is first. Idempotent.
    function wake(scope) {
      var ims = scope ? scope.querySelectorAll('img[data-srcset]') : [];
      for (var w = 0; w < ims.length; w++) {
        var im = ims[w];
        im.srcset = im.getAttribute('data-srcset');
        im.src = im.getAttribute('data-src');
        im.removeAttribute('data-srcset');
        im.removeAttribute('data-src');
      }
    }
    function viewNamed(name) { return box.querySelector('.stage__view[data-view="' + name + '"]'); }

    // ---- two worn photos (8 Oct 2026, Andrew: "i like that") ---------------
    // custom.worn_image_2 draws a second worn view, "worn2", in the same Worn
    // group. The quiet "1 / 2" pager under the photo steps between them; so do
    // the arrow keys on either photo or on the pressed Worn button, and a
    // sideways swipe on a phone. Nothing here runs without a second photo.
    var pager = root.querySelector('[data-worn-pager]');
    var pagerCount = pager ? pager.querySelector('[data-worn-count]') : null;
    var pagerSaid = pager ? pager.querySelector('[data-worn-said]') : null;
    function wornStep() {
      var cur = box.querySelector('.stage__view[data-group="worn"][data-active]');
      if (!cur) return false;
      var next = cur.getAttribute('data-view') === 'worn2' ? 'worn' : 'worn2';
      var hadFocus = document.activeElement === cur;
      show(next);
      // The photo that had focus is now hidden; focus follows to the one shown.
      // It fades in, and at the first instant of the fade it still counts as
      // hidden and refuses focus, so the focus is retried for a few frames.
      if (hadFocus) {
        var nv = viewNamed(next), tries = 0;
        (function take() {
          try { nv.focus({ preventScroll: true }); } catch (e) { nv.focus(); }
          if (document.activeElement !== nv && ++tries < 20) requestAnimationFrame(take);
        })();
      }
      return true;
    }
    function wornKey(e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return false;
      return wornStep();
    }
    if (pager) {
      pager.addEventListener('click', function (e) {
        if (e.target.closest && e.target.closest('[data-worn-go]')) wornStep();
      });
      // The sideways swipe between photographs is general now (12 Oct 2026, see
      // "the phone gallery" below); the pager's buttons and arrow keys stay.
      box.addEventListener('keydown', function (e) {
        var v = e.target.closest ? e.target.closest('.stage__view[data-group="worn"]') : null;
        if (v && wornKey(e)) e.preventDefault();
      });
    }
    // The second worn photo is NOT woken in idle time (9 Oct 2026, speed
    // re-audit): ~300 pieces have one, and it was fetched on every visit for a
    // photo seen only after pressing Worn and then stepping to it. It now
    // wakes the moment the first worn photo is shown (see show()), so the
    // step to it is still instant.
    (function () {
      var all = function () {
        var vs = views();
        for (var a = 0; a < vs.length; a++) if (vs[a].getAttribute('data-view') !== 'worn2') wake(vs[a]);
      };
      var idle = function () {
        if ('requestIdleCallback' in window) window.requestIdleCallback(all, { timeout: 3000 });
        else setTimeout(all, 500);
      };
      if (document.readyState === 'complete') idle();
      else window.addEventListener('load', idle, { once: true });
    })();

    // THE STUDIO PHOTO HANGS WHEN ITS CHAIN RUNS OFF THE TOP (3 Oct 2026).
    // The studio frames are wide and are shown whole on the square stage, so
    // a necklace whose chain leaves the top of its frame would stop in mid-air
    // where the photo ends. Once the file has loaded, its top row is read off
    // a small canvas: anything darker than the white there means a chain, and
    // .is-hung pins the photo to the top of the stage (custom.css). Read from
    // the picture, not the product type: legacy pendants carry codes like
    // "DP" and "TZP". Same-origin CDN, so the canvas is readable; if it is
    // ever not, the photo simply stays centred.
    (function () {
      var sv = viewNamed('studio');
      var im = sv ? sv.querySelector('img') : null;
      if (!im) return;
      function look() {
        if (im.hasAttribute('data-src') || !im.naturalWidth) return;
        try {
          // 480 wide and the top three rows: a fine chain is only a few
          // pixels across, and squeezed any smaller it averages out to white
          // (a 192-wide sample missed the solitaire pendant's chain at 1440).
          var c = document.createElement('canvas'); c.width = 480; c.height = 270;
          var x = c.getContext('2d');
          x.drawImage(im, 0, 0, 480, 270);
          var d = x.getImageData(0, 0, 480, 3).data;
          for (var k = 0; k < d.length; k += 4) {
            if (d[k] + d[k + 1] + d[k + 2] < 3 * 225) { sv.classList.add('is-hung'); return; }
          }
        } catch (e) {}
      }
      im.addEventListener('load', look);
      if (im.complete) look();
    })();

    function show(name) {
      var i, v, list = views();
      // Changing view ends any zoom, so the Close word never sits over the 360.
      if (typeof unzoomAll === 'function') unzoomAll();
      for (i = 0; i < list.length; i++) {
        v = list[i];
        if (v.getAttribute('data-view') === name) { wake(v); v.setAttribute('data-active', ''); }
        else {
          // Showing the first worn photo wakes the second so the step to it is
          // instant, but must still switch it off: on 9 Oct 2026 worn2 stayed
          // active on top after stepping back, so the swipe back looked stuck.
          if (name === 'worn' && v.getAttribute('data-view') === 'worn2') wake(v);
          v.removeAttribute('data-active');
        }
      }
      var btns = nav ? nav.querySelectorAll('button[data-target]') : [];
      // A view in a group (the second worn photo, 8 Oct 2026) keeps its
      // group's switch button pressed: "Worn" stays pressed on either photo.
      var av = viewNamed(name), grp = av ? av.getAttribute('data-group') : null;
      if (sw) {
        var sb = sw.querySelectorAll('button[data-go]');
        for (i = 0; i < sb.length; i++) {
          var g = sb[i].getAttribute('data-go');
          sb[i].setAttribute('aria-pressed', g === name || g === grp ? 'true' : 'false');
        }
      }
      if (pager) {
        pager.hidden = grp !== 'worn';
        var n = name === 'worn2' ? 2 : 1;
        if (pagerCount) pagerCount.textContent = n + ' / 2';
        if (pagerSaid) pagerSaid.textContent = 'Worn photo ' + n + ' of 2';
      }
      for (i = 0; i < btns.length; i++) {
        btns[i].setAttribute('aria-current',
          btns[i].getAttribute('data-target') === name ? 'true' : 'false');
      }

      // Only the video that is showing should be decoding frames.
      var vids = box.querySelectorAll('video');
      for (i = 0; i < vids.length; i++) {
        var on = vids[i].closest('.stage__view').getAttribute('data-view') === name;
        if (on && !calm) { var pr = vids[i].play(); if (pr && pr.catch) pr.catch(function () {}); }
        else if (!on) vids[i].pause();
      }

      if (note) {
        note.hidden = false;
        // Keys mean nothing on a phone (bug audit 2, 29 Sep 2026): same
        // (hover:none) test as tapOrClick above, so a touchscreen laptop keeps
        // the keys mention.
        note.textContent = name === 'spin' ? dragToTurn()
                         : name === 'video' ? 'Turning, on a loop'
                         : tapOrClick('the photograph to zoom');
      }
      if (typeof updateCount === 'function') updateCount(name);
      if (typeof spinCue === 'function') spinCue(name);
      // The 360 plays only while it is the chosen view (3 Oct 2026).
      if (spin) spin.visible(name === 'spin');
      // Focus is NOT moved into the 360 any more (3 Oct 2026, accessibility
      // pass). It used to be, so the arrow keys worked at once, but a
      // keyboard user pressing "360°" in the switch was thrown back up the
      // page into the stage, and a page whose first view is the 360 had its
      // focus taken on load. The arrow keys now turn the piece from the
      // pressed 360° button itself (see the switch below), and the 360 is
      // still one Tab stop away with its own keys.
    }

    // ---- start early -------------------------------------------------------
    // Sheet 0 when the stage is about to be on screen, in idle time; the rest
    // the moment a pointer arrives over the stage, which is the earliest honest
    // signal that someone may turn the piece. Both are no-ops once done.
    if (spin) {
      var primed = false;
      function prime() {
        if (primed) return;
        primed = true;
        var go = function () { spin.prime(); };
        if ('requestIdleCallback' in window) window.requestIdleCallback(go, { timeout: 2000 });
        else setTimeout(go, 300);
      }
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (entries[i].isIntersecting) { prime(); io.disconnect(); return; }
          }
        }, { rootMargin: '200px 0px' });
        io.observe(box);
      } else {
        prime();
      }
      // A MOUSE arriving over the stage warms the whole 360 (desktop: the
      // pointer is the earliest honest signal). A FINGER no longer does
      // (3 Oct 2026, speed pass 2): on a phone every scroll that starts on the
      // photo and every tap to zoom fired this, and pulled ~640 KB of sheets
      // for a 360 most visitors never open. A phone now loads them when the
      // 360 is pressed for (the switch's pointerdown below, then show()), or
      // on the first touch of the 360 itself. Sheet 0 is still primed above,
      // so the 360 still opens on a drawn frame.
      var warm = function (e) {
        if (e.pointerType && e.pointerType !== 'mouse') return;
        box.removeEventListener('pointerenter', warm);
        spin.open();
      };
      box.addEventListener('pointerenter', warm);
    }

    if (nav) {
      nav.addEventListener('click', function (e) {
        var b = e.target.closest ? e.target.closest('button[data-target]') : null;
        if (b) show(b.getAttribute('data-target'));
      });
    }

    // ---- the Photo | 360 switch (30 Sep 2026, Andrew: "Do option 2") -------
    // Replaces the thumbnail rail on the product page. It sits at the top of
    // the green plate (.xplate), so on a phone it is on the first screen and
    // says what 360 does before anyone taps it. Built here, not in Liquid, so
    // it exists only where the viewer is running: the rail stays in the markup
    // and is hidden by the has-switch class this adds, so a page without
    // JavaScript still has working thumbnails. Only for a piece with a 360 or
    // a worn photograph.
    var sw = null;
    (function () {
      // BUILT FROM THE RAIL, IN ITS ORDER (generic since 2 Oct 2026): one
      // button per rail tile, labelled by the tile's data-label. Photo, Worn,
      // Studio (custom.studio_image, 3 Oct 2026), then Video (custom.spin_video)
      // or 360; a 3D view added to the rail later
      // gets its button with no change here. Any two views make a switch; a
      // piece with only a photograph gets none.
      var tiles = nav ? nav.querySelectorAll('button[data-target]') : [];
      var pdp = root.closest ? root.closest('.pdp') : null;
      var plate = pdp ? pdp.querySelector('.xplate') : null;
      if (tiles.length < 2 || !plate) return;
      var SAID = { worn: 'Worn photo', studio: 'Studio photo', spin: '360° view', video: 'Video of the piece turning' };
      var TURN = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 12a8 8 0 1 1-2.34-5.66"/><path d="M18 3v3.5h-3.5"/></svg>';
      sw = document.createElement('div');
      // Four views (Photo | Worn | Studio | 360°) do not fit a phone as a
      // pill sized to its words; .pdp-switch--fill spreads them across the
      // plate in equal parts instead (custom.css).
      sw.className = 'pdp-switch' + (tiles.length > 3 ? ' pdp-switch--fill' : '');
      sw.setAttribute('role', 'group');
      sw.setAttribute('aria-label', 'Choose a view');
      var html = '';
      for (var t = 0; t < tiles.length; t++) {
        var go = tiles[t].getAttribute('data-target');
        var lab = tiles[t].getAttribute('data-label') || go;
        // Spoken names a little fuller than the visible words (3 Oct 2026,
        // accessibility pass); each still CONTAINS the visible word, so voice
        // control ("press Worn") keeps working.
        var said = SAID[go] || lab;
        html += '<button type="button" data-go="' + go + '" aria-pressed="' + (t === 0 ? 'true' : 'false') + '"' +
          (said !== lab ? ' aria-label="' + said + '"' : '') + '>' +
          (go === 'spin' ? TURN : '') + lab + '</button>';
      }
      sw.innerHTML = html;
      sw.addEventListener('click', function (e) {
        var b = e.target.closest ? e.target.closest('button[data-go]') : null;
        if (b) {
          show(b.getAttribute('data-go'));
          /* SO THE CHANGE IS SEEN (2 Nov 2026, Shopify SimGym run 2: "clicking Worn repeatedly changed nothing" on bracelets).
             The switch sits under the photo, and on a short or scrolled screen the photo itself can be partly out of
             view when it changes. If the stage's top edge is above the screen, bring it back into view. */
          try {
            var sr = box.getBoundingClientRect();
            if (sr.top < 0 || sr.bottom < 120) box.scrollIntoView({ block: 'start', behavior: calm ? 'auto' : 'smooth' });
          } catch (er) {}
        }
      });
      // A head start on the press, before the click lands: the Worn photo
      // and the 360's sheets begin fetching on pointerdown.
      sw.addEventListener('pointerdown', function (e) {
        var b = e.target.closest ? e.target.closest('button[data-go]') : null;
        if (!b) return;
        var go = b.getAttribute('data-go');
        wake(viewNamed(go));
        if (go === 'worn') wake(viewNamed('worn2'));
        if (go === 'spin' && spin) spin.open();
      });
      // The pressed 360° button turns the piece with the arrow keys, so a
      // keyboard user can turn it without leaving the switch.
      sw.addEventListener('keydown', function (e) {
        var b = e.target.closest ? e.target.closest('button[data-go="spin"][aria-pressed="true"]') : null;
        if (b && spin && spin.key(e)) e.preventDefault();
        // The pressed Worn button steps between two worn photos the same way.
        var w = e.target.closest ? e.target.closest('button[data-go="worn"][aria-pressed="true"]') : null;
        if (w && pager && wornKey(e)) e.preventDefault();
      });
      plate.insertBefore(sw, plate.firstChild);
      root.classList.add('has-switch');
    })();

    // ---- zoom on a photograph -------------------------------------------
    // The view is a <button>, so Enter and Space toggle it with no keydown
    // handler of ours. The pointer only steers the origin; it never toggles.
    // `aria-pressed` and the label are kept in step with the class. Without
    // them the button announces "enlarge" whether or not it already is
    // zoomed, and there is no way to hear that anything happened — the zoom is
    // a purely visual transform, so the class change is silent.
    function markZoom(v) {
      var on = v.classList.contains('is-zoomed');
      var base = v.getAttribute('data-label-base') || '';
      v.setAttribute('aria-pressed', on ? 'true' : 'false');
      /* "enlarge", not "tap to zoom" or "click to zoom": this is the button's
       * accessible NAME, and a name that describes the input device is wrong on
       * a switch, a keyboard or a screen reader's own gesture. The visible hint
       * under the stage is the place that names the gesture, and it does. */
      if (base) v.setAttribute('aria-label', base + (on ? ', close zoom' : ', enlarge'));
    }

    // While a photograph is zoomed: the Close word shows in the stage's corner
    // and the hint under the stage says how to close it.
    var unzoomBtn = box.querySelector('[data-unzoom]');
    var lastZoomed = null;
    function syncZoom() {
      var any = !!box.querySelector('.stage__view--photo.is-zoomed');
      if (unzoomBtn) {
        // Close hides itself once the zoom ends. If it had focus, focus would
        // drop to the top of the page (3 Oct 2026, accessibility pass), so it
        // goes back to the photograph that was zoomed first.
        if (!any && lastZoomed && document.activeElement === unzoomBtn) {
          try { lastZoomed.focus({ preventScroll: true }); } catch (e) { lastZoomed.focus(); }
        }
        unzoomBtn.hidden = !any;
      }
      if (note && !note.hidden && box.querySelector('.stage__view--photo[data-active]')) {
        note.textContent = any ? tapOrClick('anywhere to close') : tapOrClick('the photograph to zoom');
      }
    }

    box.addEventListener('click', function (e) {
      var v = e.target.closest ? e.target.closest('.stage__view--photo') : null;
      if (v) { v.classList.toggle('is-zoomed'); if (v.classList.contains('is-zoomed')) lastZoomed = v; markZoom(v); syncZoom(); }
    });
    box.addEventListener('pointermove', function (e) {
      // Guarded on is-zoomed: without it, every mouse move across an unzoomed
      // photograph would set a transform-origin nobody can see and thrash
      // style recalculation on the largest image on the page.
      var v = e.target.closest ? e.target.closest('.stage__view--photo.is-zoomed') : null;
      if (!v) return;
      var r = v.getBoundingClientRect();
      v.style.setProperty('--zx', ((e.clientX - r.left) / r.width * 100) + '%');
      v.style.setProperty('--zy', ((e.clientY - r.top) / r.height * 100) + '%');
    });
    function unzoomAll() {
      var z = box.querySelectorAll('.stage__view--photo.is-zoomed');
      for (var i = 0; i < z.length; i++) { z[i].classList.remove('is-zoomed'); markZoom(z[i]); }
      syncZoom();
    }
    /* TAP ANYWHERE TO CLOSE (2 Oct 2026). Any press outside the zoomed photo
     * ends the zoom: the Close word, the stage around it, the rest of the page.
     * A press ON a photograph is left to the toggle above, which runs first
     * because it is nearer the target; without this guard the press that
     * zooms in would bubble here and close it straight away. */
    document.addEventListener('click', function (e) {
      if (!box.querySelector('.stage__view--photo.is-zoomed')) return;
      if (e.target.closest && e.target.closest('.stage__view--photo')) return;
      unzoomAll();
    });
    /* A MOUSE leaving the photograph ends the zoom; a finger does not. On a
     * touch screen the browser fires pointerleave at the END OF EVERY TAP,
     * before the click — so the tap that meant "close zoom" first un-zoomed
     * here and then the click re-zoomed it, and a zoomed photo on a phone could
     * never be closed (29 Sep 2026, bug hunt). Touch and pen close by tapping
     * again, which is the click handler above. */
    box.addEventListener('pointerleave', function (e) {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      unzoomAll();
    });
    /* Esc closes the zoom too, as it closes every other layer on the site. */
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' || e.key === 'Esc') unzoomAll();
    });

    // ---- "Drag to turn" (12 Oct 2026, Andrew) ------------------------------------
    // A tiny text cue over the 360, once per visit: shown when the 360 first opens,
    // gone after the first touch or drag, or after four seconds. No icon. The state
    // is in sessionStorage, every access in try/catch; with storage off it shows once
    // per page. Styles are in assets/gl-touches.css (its fade is off under reduced motion).
    var cueEl = null, cueDone = false, cueTimer = 0;
    function cueSeen() { try { return window.sessionStorage.getItem('gl.spincue') === '1'; } catch (e) { return false; } }
    function cueMark() { try { window.sessionStorage.setItem('gl.spincue', '1'); } catch (e) {} }
    function cueHide() {
      if (!cueEl) return;
      clearTimeout(cueTimer);
      cueEl.classList.remove('is-on');
    }
    function spinCue(name) {
      if (name !== 'spin') { cueHide(); return; }
      if (cueDone || cueSeen()) { cueDone = true; return; }
      var view = viewNamed('spin');
      if (!view) return;
      cueDone = true; cueMark();
      cueEl = document.createElement('span');
      cueEl.className = 'stage__cue';
      cueEl.setAttribute('aria-hidden', 'true');
      cueEl.textContent = dragToTurn().split(',')[0];
      view.appendChild(cueEl);
      view.addEventListener('pointerdown', cueHide, { once: true });
      requestAnimationFrame(function () { cueEl.classList.add('is-on'); });
      cueTimer = setTimeout(cueHide, 4000);
    }

    // ---- the phone gallery (12 Oct 2026, Andrew round 3) -----------------------
    // On a touch phone: swipe sideways between the photographs (Photo, Worn,
    // the second Worn, Studio), a quiet "2 / 5" in the corner, and a tap opens
    // the photograph full screen with pinch zoom (Close and Esc close it). The
    // Photo | Worn | Video | 360° switch is untouched, and the video and the
    // 360 are NOT swipe targets: dragging the 360 is its whole job. A mouse, or a
    // desktop-width window, behaves exactly as before.
    // A phone-width window on a touch device. `pointer: coarse` alone is not
    // reported by every touch browser, so a touch point also counts.
    var narrowQ = window.matchMedia ? window.matchMedia('(max-width: 900px)') : null;
    var coarseQ = window.matchMedia ? window.matchMedia('(pointer: coarse)') : null;
    function isPhone() {
      return !!(narrowQ && narrowQ.matches && ((coarseQ && coarseQ.matches) || (navigator.maxTouchPoints || 0) > 0));
    }
    function photoNames() {
      var l = box.querySelectorAll('.stage__view--photo'), o = [];
      for (var i = 0; i < l.length; i++) o.push(l[i].getAttribute('data-view'));
      return o;
    }
    var countEl = document.createElement('span');
    countEl.className = 'stage__count';
    countEl.setAttribute('aria-hidden', 'true');
    countEl.hidden = true;
    box.appendChild(countEl);
    // TWO COUNTERS BECAME ONE (15 Oct 2026, phone audit): on the Worn view a phone showed the worn pager's
    // "1 / 2" and this "2 / 3" at once. The swipe moves through ALL the photographs, so this is the one that
    // stays; while it shows, the box carries .has-gcount and the pager's visible count steps aside (it is
    // still spoken: the "Worn photo 2 of 2" line is a live region). The pager's arrows are unchanged.
    function updateCount(name) {
      var names = photoNames(), i = names.indexOf(name);
      if (names.length < 2 || i < 0 || !isPhone()) { countEl.hidden = true; box.classList.remove('has-gcount'); return; }
      countEl.textContent = (i + 1) + ' / ' + names.length;
      countEl.hidden = false;
      box.classList.add('has-gcount');
    }
    function photoStep(dir) {
      var cur = box.querySelector('.stage__view--photo[data-active]');
      if (!cur) return false;
      var names = photoNames(), i = names.indexOf(cur.getAttribute('data-view')) + dir;
      if (i < 0 || i >= names.length) return false;
      show(names[i]);
      return true;
    }
    // Left and Right arrow keys step the photos on a computer (13 Oct 2026, Andrew). Only
    // when focus is on the page itself or inside the gallery, never while someone types in a
    // field, holds a modifier key, or has the bag panel, a menu or the full-size viewer open.
    // A key the worn-photo pager already handled arrives defaultPrevented and is skipped.
    document.addEventListener('keydown', function (e) {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (isPhone()) return;
      var t = e.target, tag = t && t.tagName ? t.tagName.toLowerCase() : '';
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || (t && t.isContentEditable)) return;
      if (t && t !== document.body && t !== document.documentElement && !(box.contains && box.contains(t))) return;
      var root = document.documentElement;
      if (root.classList.contains('bag-open') || document.querySelector('.gl-lb-open, [data-lightbox][open]')) return;
      if (photoStep(e.key === 'ArrowRight' ? 1 : -1)) e.preventDefault();
    });
    (function () {
      var sx = 0, sy = 0, tracking = false, swipedAt = 0;
      box.addEventListener('pointerdown', function (e) {
        var v = e.target.closest ? e.target.closest('.stage__view--photo[data-active]') : null;
        tracking = !!v && e.pointerType !== 'mouse' && isPhone();
        sx = e.clientX; sy = e.clientY;
      });
      box.addEventListener('pointerup', function (e) {
        if (!tracking) return;
        tracking = false;
        var dx = e.clientX - sx, dy = e.clientY - sy;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5 && photoStep(dx < 0 ? 1 : -1)) swipedAt = Date.now();
      });
      box.addEventListener('pointercancel', function () { tracking = false; });
      // A swipe is not a tap: the click that may follow it must open nothing.
      box.addEventListener('click', function (e) {
        if (swipedAt && Date.now() - swipedAt < 500) { e.stopImmediatePropagation(); e.preventDefault(); swipedAt = 0; }
      }, true);
      // A tap on a photograph opens the full-screen view instead of the in-place zoom.
      box.addEventListener('click', function (e) {
        if (!isPhone()) return;
        var v = e.target.closest ? e.target.closest('.stage__view--photo') : null;
        if (!v) return;
        e.stopImmediatePropagation(); e.preventDefault();
        openLightbox(v);
      }, true);
    })();

    var lightbox = null;
    function openLightbox(view) {
      var im = view.querySelector('img');
      if (!im) return;
      if (!lightbox) lightbox = buildLightbox();
      lightbox.open(im, view);
    }
    function buildLightbox() {
      var css = document.createElement('style');
      css.textContent =
        '.gl-lb{position:fixed;inset:0;z-index:1100;background:#14120f;touch-action:none;overscroll-behavior:contain}' +
        '.gl-lb[hidden]{display:none}' +
        '.gl-lb__stage{position:absolute;inset:0;overflow:hidden;display:flex;align-items:center;justify-content:center;touch-action:none}' +
        '.gl-lb__img{max-width:100%;max-height:100%;object-fit:contain;transform-origin:50% 50%;will-change:transform;user-select:none;-webkit-user-drag:none}' +
        '.gl-lb__close{position:absolute;top:max(12px,env(safe-area-inset-top));right:12px;z-index:2;min-height:44px;padding:0 16px;border:1px solid rgba(255,255,255,.55);background:rgba(20,18,15,.6);color:#fff;font:500 15px/1 var(--font-sans,sans-serif);cursor:pointer}' +
        '.gl-lb__close:focus-visible{outline:2px solid #fff;outline-offset:2px}' +
        'html.gl-lb-open{overflow:hidden}';
      document.head.appendChild(css);
      var wrap = document.createElement('div');
      wrap.className = 'gl-lb'; wrap.hidden = true;
      wrap.setAttribute('role', 'dialog'); wrap.setAttribute('aria-modal', 'true'); wrap.setAttribute('aria-label', 'Photograph, full screen');
      var closeBtn = document.createElement('button');
      closeBtn.type = 'button'; closeBtn.className = 'gl-lb__close'; closeBtn.textContent = 'Close';
      var area = document.createElement('div'); area.className = 'gl-lb__stage';
      var img = document.createElement('img'); img.className = 'gl-lb__img'; img.draggable = false;
      area.appendChild(img); wrap.appendChild(area); wrap.appendChild(closeBtn);
      document.body.appendChild(wrap);

      var s = 1, tx = 0, ty = 0, opener = null;
      var ptrs = {}, startDist = 0, startS = 1, p0 = null, tx0 = 0, ty0 = 0, lastTap = 0, moved = 0;
      function apply() { img.style.transform = 'translate(' + tx + 'px,' + ty + 'px) scale(' + s + ')'; }
      function clamp() {
        if (s <= 1) { s = 1; tx = 0; ty = 0; return; }
        if (s > 4) s = 4;
        var r = area.getBoundingClientRect();
        var mx = Math.max(0, (img.offsetWidth * s - r.width) / 2), my = Math.max(0, (img.offsetHeight * s - r.height) / 2);
        tx = Math.min(mx, Math.max(-mx, tx)); ty = Math.min(my, Math.max(-my, ty));
      }
      function count() { var n = 0; for (var k in ptrs) if (ptrs.hasOwnProperty(k)) n++; return n; }
      function two() { var a = [], k; for (k in ptrs) if (ptrs.hasOwnProperty(k)) a.push(ptrs[k]); return a; }
      function dist(a, b) { return Math.sqrt(Math.pow(a.x - b.x, 2) + Math.pow(a.y - b.y, 2)); }
      area.addEventListener('pointerdown', function (e) {
        try { area.setPointerCapture(e.pointerId); } catch (x) {}
        ptrs[e.pointerId] = { x: e.clientX, y: e.clientY };
        moved = 0;
        if (count() === 2) { var t = two(); startDist = dist(t[0], t[1]) || 1; startS = s; }
        else { p0 = { x: e.clientX, y: e.clientY }; tx0 = tx; ty0 = ty; }
      });
      area.addEventListener('pointermove', function (e) {
        if (!ptrs[e.pointerId]) return;
        ptrs[e.pointerId] = { x: e.clientX, y: e.clientY };
        if (count() === 2) {
          var t = two();
          s = startS * dist(t[0], t[1]) / startDist; clamp(); apply();
        } else if (p0 && s > 1) {
          tx = tx0 + (e.clientX - p0.x); ty = ty0 + (e.clientY - p0.y); clamp(); apply();
        }
        if (p0) moved = Math.max(moved, Math.abs(e.clientX - p0.x) + Math.abs(e.clientY - p0.y));
      });
      function lift(e) {
        var was = count();
        delete ptrs[e.pointerId];
        if (was === 1 && moved < 8) {
          var now = Date.now();
          if (now - lastTap < 300) { s = s > 1 ? 1 : 2.5; clamp(); apply(); lastTap = 0; } else lastTap = now;
        }
        if (count() === 1) { var r = two()[0]; p0 = { x: r.x, y: r.y }; tx0 = tx; ty0 = ty; }
        if (!count()) p0 = null;
      }
      area.addEventListener('pointerup', lift);
      area.addEventListener('pointercancel', lift);

      function onKey(e) {
        if (e.key === 'Escape' || e.key === 'Esc') { close(); return; }
        if (e.key === 'Tab') { e.preventDefault(); closeBtn.focus(); }
      }
      function open(im, view) {
        opener = view;
        var src = im.currentSrc || im.src;
        src = /([?&])width=\d+/.test(src) ? src.replace(/([?&])width=\d+/, '$1width=1800') : src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=1800';
        img.src = src; img.alt = im.alt || '';
        s = 1; tx = 0; ty = 0; ptrs = {}; apply();
        wrap.hidden = false;
        document.documentElement.classList.add('gl-lb-open');
        document.addEventListener('keydown', onKey);
        closeBtn.focus();
      }
      function close() {
        if (wrap.hidden) return;
        wrap.hidden = true;
        document.documentElement.classList.remove('gl-lb-open');
        document.removeEventListener('keydown', onKey);
        if (opener && opener.focus) { try { opener.focus({ preventScroll: true }); } catch (x) { opener.focus(); } }
        opener = null;
      }
      closeBtn.addEventListener('click', close);
      return { open: open, close: close };
    }

    // Whatever the markup marked active is the truth; the switcher only has to
    // agree with it. Deriving the initial view here rather than assuming 'video'
    // means a piece with no video still starts on something real.
    var start = box.querySelector('.stage__view[data-active]');
    show(start ? start.getAttribute('data-view')
               : (views()[0] || {}).getAttribute
                 ? views()[0].getAttribute('data-view') : 'spin');
  }

  function Spin(el, bar, fill) {
    var urls = (el.getAttribute('data-sheets') || '').split(',').filter(Boolean);
    var N = parseInt(el.getAttribute('data-frames'), 10) || 72;
    var PER = parseInt(el.getAttribute('data-per-sheet'), 10) || 25;
    var COLS = parseInt(el.getAttribute('data-cols'), 10) || 5;
    var ROWS = parseInt(el.getAttribute('data-rows'), 10) || 5;
    var STRIDED = el.getAttribute('data-packing') === 'strided';
    var NUM_SHEETS = urls.length;
    var cv = el.querySelector('canvas');
    if (!urls.length || !cv) return null;

    // Which (sheet, cell) holds frame f. Mirrors scripts/spin_packing.py's
    // locate() exactly — HANDOFF.md item 1 is explicit that this and the
    // build script scrambling the turn is the failure mode of getting them
    // out of sync, so if you touch this, touch that file with it.
    //   consecutive: sheet s holds frames s*PER .. s*PER+PER-1
    //   strided:     sheet s holds frames s, s+NUM_SHEETS, s+2*NUM_SHEETS...
    function locate(f) {
      return STRIDED
        ? { sheet: f % NUM_SHEETS, k: Math.floor(f / NUM_SHEETS) }
        : { sheet: Math.floor(f / PER), k: f % PER };
    }

    var ctx = cv.getContext('2d');
    // The rail's 360 tile (Package 19): a <canvas> over the hero thumbnail,
    // painted once with the frame nearest a quarter turn so the tile reads
    // as "this turns" rather than as Photo 1 a second time.
    var root = el.closest('[data-stage-root]');
    var thumbCv = root ? root.querySelector('[data-spin-thumb]') : null;
    // THE THUMBNAIL IS PAINTED FROM A SMALL CDN COPY OF ITS SHEET, 18 Sep 2026.
    // prime() used to fetch the full sheet 0 - 342KB, the largest file on the
    // page, measured on the live ruby ring - on every product page load, so
    // that a 72px tile in the rail could show one frame of it. The sheets are
    // Shopify Files, so `?width=600` returns the same grid at 600px across:
    // 200px cells for a 200px canvas, at about a tenth of the bytes. The full
    // sheets still load when the customer opens the 360 (open()), and if the
    // small copy fails the tile is painted from sheet 0 when that lands.
    function paintThumb(im, k) {
      if (!thumbCv || !im || !im.naturalWidth) return;
      var tc = thumbCv.getContext('2d');
      var cw = im.naturalWidth / COLS, ch = im.naturalHeight / ROWS;
      var s = thumbCv.width / cw, h = ch * s, y = (thumbCv.height - h) / 2;
      var sx = (k % COLS) * cw, sy = Math.floor(k / COLS) * ch;
      var strip = Math.max(1, Math.min(6, Math.floor(ch / 40)));
      tc.clearRect(0, 0, thumbCv.width, thumbCv.height);
      // The same edge fill as the stage, so the tile is not a strip on white.
      if (y > 0) {
        if ('filter' in tc) tc.filter = 'blur(6px)';
        tc.drawImage(im, sx, sy, cw, strip, -12, -12, thumbCv.width + 24, y + 14);
        tc.drawImage(im, sx, sy + ch - strip, cw, strip, -12, y + h - 2, thumbCv.width + 24, thumbCv.height - (y + h) + 14);
        if ('filter' in tc) tc.filter = 'none';
      }
      tc.drawImage(im, sx, sy, cw, ch, 0, y, thumbCv.width, h);
      thumbCv.hidden = false;
    }
    function paintThumbFromSheets() {
      var f = nearestDrawable(Math.round(N / 4));
      if (f < 0) return;
      var loc = locate(f);
      paintThumb(sheets[loc.sheet], loc.k);
    }
    var sheets = [], ready = false, cur = 0, raf = 0, started = false;
    // 6px of pointer travel per frame: 72 frames in ~430px, a turn in one
    // comfortable drag. It was 11 (792px), which read as stiff.
    var STEP = 6;
    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // The cell is measured off the sheet rather than passed in: every sheet is a
    // full COLS x ROWS grid even when the last one is part empty, so the cell is
    // exactly naturalWidth/COLS by naturalHeight/ROWS and no metafield has to
    // carry geometry the image already states.
    // THE CANVAS TAKES THE STAGE'S SHAPE, NOT THE FRAME'S (19 Sep 2026).
    // Every frame is a wide strip cropped tight to the piece (1480 x 448-790
    // across the live catalogue) and the stage is square, so a canvas the
    // frame's shape sat in the square with two thirds of it blank white above
    // and below - Andrew: "why is there just blank white space around the 360".
    // The canvas is now as wide as a frame and as tall as the stage is at that
    // width; draw() paints the frame centred and fills the rest with the
    // frame's own backdrop, see there.
    var CVH = 0, DW = 0, DH = 0;
    // FAST LOAD (29 Sep 2026). A small copy of every sheet lands first and the
    // sharp copies replace it, so the canvas is sized to how big it is SHOWN
    // (capped at 2x), never to whichever copy arrives first: that would lock it
    // at the preview's 240px cells and the sharp sheets could not sharpen it.
    function measure(im) {
      var cw = im.naturalWidth / COLS, ch = im.naturalHeight / ROWS;
      var box = el.getBoundingClientRect();
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      DW = Math.max(320, Math.round((box.width || cw) * dpr));
      DH = Math.round(DW * ch / cw);
      var ratio = box.width > 0 && box.height > 0 ? box.height / box.width : 1;
      CVH = Math.max(DH, Math.round(DW * ratio));
      if (cv.width !== DW || cv.height !== CVH) { cv.width = DW; cv.height = CVH; }
      BLUR = 'blur(' + Math.max(8, Math.round(28 * DW / 1480)) + 'px)';
    }
    var BLUR = 'blur(28px)';
    function fillBands(c, im, sx, sy, cw, ch, dy) {
      var strip = Math.max(2, Math.min(12, Math.floor(ch / 24)));
      var pad = 64;
      var hasFilter = 'filter' in c;
      if (hasFilter) c.filter = BLUR;
      if (dy > 0) {
        c.drawImage(im, sx, sy, cw, strip, -pad, -pad, cv.width + pad * 2, dy + pad + 2);
      }
      var bottom = dy + DH;
      if (bottom < cv.height) {
        c.drawImage(im, sx, sy + ch - strip, cw, strip, -pad, bottom - 2, cv.width + pad * 2, cv.height - bottom + pad + 2);
      }
      if (hasFilter) c.filter = 'none';
    }

    function nearestDrawable(f) {
      if (drawable(f)) return f;
      for (var k = 1; k <= N / 2; k++) {
        var a = (f + k) % N, b = ((f - k) % N + N) % N;
        if (drawable(a)) return a;
        if (drawable(b)) return b;
      }
      return -1;
    }

    // Frame f into context c: the backdrop bands (bands = true) and the piece.
    function paintFrame(c, f, bands) {
      var loc = locate(f), im = sheets[loc.sheet];
      if (!DW) measure(im);
      var cw = im.naturalWidth / COLS, ch = im.naturalHeight / ROWS;
      var sx = (loc.k % COLS) * cw, sy = Math.floor(loc.k / COLS) * ch;
      var dy = Math.round((cv.height - DH) / 2);
      if (bands) fillBands(c, im, sx, sy, cw, ch, dy);
      c.drawImage(im, sx, sy, cw, ch, 0, dy, cv.width, DH);
    }
    function draw() {
      var f = nearestDrawable(cur);
      if (f < 0) return;
      paintFrame(ctx, f, true);
    }

    // Whether frame f can be painted right now. Under consecutive packing,
    // what is loaded is a prefix of the FRAMES only by accident of sheet
    // order: sheet 0 holds 0..PER-1 and nothing past it exists until sheet 1
    // lands. Under strided packing this is no longer true — sheet 0 alone
    // covers every angle, coarsely — but the same drawable() still works
    // unchanged, because it asks "is THIS frame's sheet here", not "have we
    // reached this frame yet": locate() already puts frame f on the right
    // sheet for whichever packing is in effect.
    function drawable(f) {
      var im = sheets[locate(f).sheet];
      return !!(im && im.complete && im.naturalWidth);
    }

    function step(d) {
      // THE LOGICAL POSITION ALWAYS ADVANCES; draw() paints the nearest loaded
      // frame. 10 Sep 2026, and this replaces a rule that was right for the
      // old packing and wrong for the new one.
      //
      // The old rule refused any step to a frame whose sheet had not arrived
      // ("hold at the edge of what is loaded"), written 31 Aug for consecutive
      // packing, where the loaded frames were a contiguous arc and the angle
      // readout had to stay honest. Strided packing (2 Sep) spreads sheet 0
      // around the whole circle precisely so one sheet is a coarse complete
      // turn — but frames 1 to 5 live on sheets 1 to 5, so a step of one from
      // frame 0 was always refused and sheet 0's turn could never be reached.
      // Measured 10 Sep in a headless run: 270px of drag with sheet 0 alone
      // produced almost no movement. The readout is gone (Package 15), so
      // honesty of a number is no longer a reason; nearestDrawable() is what
      // makes a partly loaded spin turn coarsely instead of not at all.
      cur = ((cur + d) % N + N) % N;
      if (!raf) raf = requestAnimationFrame(function () { raf = 0; draw(); say(); });
    }
    // The angle, for a screen reader: gl-spin.liquid makes the 360 a slider
    // (3 Oct 2026, accessibility pass). Written once per painted frame, not
    // per step, so a fast drag does not flood the attribute.
    function say() {
      var deg = Math.round(cur * 360 / N) % 360;
      el.setAttribute('aria-valuenow', deg);
      el.setAttribute('aria-valuetext', deg + ' degrees');
    }

    // One sheet, once. `sheets[i]` doubles as the "already requested" flag, so
    // prime() and open() can both ask for sheet 0 and it is fetched one time.
    // Two copies of every sheet. The PREVIEW (240px cells, ~16 KB each as WebP)
    // for all sheets in parallel makes the whole turn draggable in under a
    // second on a phone; the SHARP copy, sized to the screen (740px cells for a
    // phone or 1x screen, full size for 2x desktop), replaces each one when it
    // lands, with no jump because the canvas does not change size. Measured on a
    // throttled phone: smooth drag 4.6 s -> 0.86 s, 2.21 MB -> 1.13 MB. The CDN
    // does the resizing and the WebP, so no extra files exist.
    var PREV_W = COLS * 240, asked = [], previews = 0, sharpOn = false, primed = false;
    function withW(u, w) { return w ? u + (u.indexOf('?') > -1 ? '&' : '?') + 'width=' + w : u; }
    function sharpW() {
      var box = el.getBoundingClientRect(), dpr = Math.min(window.devicePixelRatio || 1, 2);
      return (box.width || 0) * dpr > 777 ? 0 : COLS * 740;
    }
    function landed(i, im) {
      var have = sheets[i];
      if (!have || !have.naturalWidth || im.naturalWidth > have.naturalWidth) sheets[i] = im;
      if (i === 0 && !ready) { ready = true; if (hint) hint.classList.remove('is-wait'); }
      if (thumbCv && thumbCv.hidden) paintThumbFromSheets();
      baseF = -1;
      if (!loopRaf) draw();
    }
    function fetchSheet(i) {
      if (asked[i]) return;
      asked[i] = true;
      var im = new Image();
      im.decoding = 'async';
      im.onload = function () { landed(i, im); done(); };
      im.onerror = done;
      function done() {
        previews++;
        if (fill) fill.style.width = (previews / urls.length * 100) + '%';
        if (previews >= urls.length) { if (bar) bar.hidden = true; sharpen(); }
      }
      im.src = withW(urls[i], PREV_W);
    }
    // sharp copies only after every preview is in, so they never compete with
    // the previews for a phone's bandwidth
    function sharpen() {
      if (sharpOn) return;
      sharpOn = true;
      var w = sharpW();
      urls.forEach(function (u, i) {
        var im = new Image();
        im.decoding = 'async';
        im.onload = function () { landed(i, im); };
        im.src = withW(u, w);
      });
    }

    // The preview of the sheet holding the rail tile's frame, when the stage
    // nears the viewport. It paints the tile and, under strided packing, is
    // sheet 0, so the 360 opens on a drawn frame instead of a blank box.
    function prime() {
      if (!thumbCv || primed) return;
      primed = true;
      fetchSheet(locate(Math.round(N / 4)).sheet);
    }

    // Everything. Called on the first pointer over the stage, or when the 360
    // view is chosen. The bar shows only while previews are still to come.
    function open() {
      if (started) return;
      started = true;
      if (previews < urls.length && bar) {
        bar.hidden = false;
        if (fill) fill.style.width = (previews / urls.length * 100) + '%';
      }
      for (var i = 0; i < urls.length; i++) fetchSheet(i);
    }

    // ---- momentum ----------------------------------------------------------
    // The last few pointer samples give a velocity in px/ms; on release the
    // turn coasts, the velocity decaying by ~10% every 16ms (gone in ~600ms),
    // and frames step whenever the accumulated travel crosses STEP. Any new
    // pointerdown cancels it. Off under reduced motion: the ring then stops
    // where the finger leaves it, exactly as before.
    var samples = [], coastRaf = 0;
    function sample(x) {
      var now = performance.now();
      samples.push({ x: x, t: now });
      while (samples.length > 5) samples.shift();
    }
    function velocity() {
      if (samples.length < 2) return 0;
      var a = samples[0], b = samples[samples.length - 1];
      var dt = b.t - a.t;
      return dt > 0 ? (b.x - a.x) / dt : 0;
    }
    function stopCoast() { if (coastRaf) { cancelAnimationFrame(coastRaf); coastRaf = 0; } }
    function coast(v) {
      if (reduceMotion || Math.abs(v) < 0.15) return;
      var carry = 0, last = performance.now();
      function tick(now) {
        var dt = Math.min(now - last, 48); last = now;
        carry += v * dt;
        v *= Math.pow(0.9, dt / 16);
        while (Math.abs(carry) >= STEP) {
          if (carry > 0) { step(-1); carry -= STEP; } else { step(1); carry += STEP; }
        }
        if (Math.abs(v) >= 0.02) coastRaf = requestAnimationFrame(tick);
        else coastRaf = 0;
      }
      coastRaf = requestAnimationFrame(tick);
    }

    // ---- hint (29 Sep 2026, Andrew). A ring-and-arrow mark over the 360,
    // faded after the first turn by hand and gone for the visit. Made here,
    // absolutely positioned, so it can never shift the layout; same drawing as
    // the rail's 360 tile. It no longer turns with the piece (3 Oct 2026): the
    // piece now turns on its own, so the mark only says "you can drag this".
    var SEEN = 'gl-spun';
    function seen() { try { return sessionStorage.getItem(SEEN) === '1'; } catch (e) { return false; } }
    var hint = document.createElement('span');
    hint.className = 'spin-hint' + (seen() ? ' is-gone' : '') + (ready ? '' : ' is-wait');
    hint.setAttribute('aria-hidden', 'true');
    hint.innerHTML = '<svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round">'
      + '<path d="M20 12a8 8 0 1 1-2.34-5.66"/><path d="M18 3v3.5h-3.5"/></svg>';
    el.appendChild(hint);

    var handled = false;
    function spun() {
      if (handled) return;
      handled = true;
      hint.classList.add('is-gone');
      try { sessionStorage.setItem(SEEN, '1'); } catch (e) {}
    }

    // ---- THE 360 PLAYS LIKE A VIDEO (3 Oct 2026, Andrew: "basically like a
    // video, but you can drag it around and pause it"). Replaces the one slow
    // arrival turn. While the 360 is the chosen view it turns on its own, one
    // turn every TURN ms at an even speed, round and round, until the shopper
    // touches it, drags it or turns it with the keys; then it stays exactly
    // where they left it, and the play control starts it again.
    //
    // SMOOTH, NOT STEPPED: a 48-frame spin at this speed changes frame about
    // seven times a second, which reads as stop-motion. Between two frames the
    // next one is laid over the last at the fraction of the way there, so the
    // piece glides. The backdrop bands are blurred, the costly part, so each
    // frame is composed once into `base` and the glide only copies it and lays
    // one frame over it; painting is capped near 30 a second.
    //
    // It runs only when there is something to see and someone to see it: the
    // 360 chosen (visible()), at least a fifth of it on screen, the tab in
    // front, and the shopper has not paused it. It starts once every frame of
    // the turn can be painted, so the loop never jumps between the coarse
    // early frames. Reduced motion: it never starts by itself; the play
    // control is there, and the motion is then the shopper's choice.
    var TURN = 7000;
    // THE FIRST LOOK IS THE FRONT (15 Oct 2026, phone audit: "opens on the underside"). Every turn starts on
    // frame 0, which is the front of the piece on the 13 pieces checked that day; the loop used to begin
    // turning the instant the last frame arrived, so a photograph taken a second or two later caught the
    // underside. It now stays on frame 0 for 900 ms the FIRST time it starts, then turns. Resuming after a
    // pause or a scroll away does not wait again.
    var HOLD_FIRST = 900, holdUntil = 0, firstRun = true;
    var pos = 0, loopRaf = 0, lastT = 0, lastPaint = 0, waitT = 0;
    var active = false, inView = true, pageOn = !document.hidden, held = reduceMotion;
    var base = document.createElement('canvas'), baseCtx = base.getContext('2d'), baseF = -1;
    function allHere() {
      for (var k = 0; k < N; k++) if (!drawable(k)) return false;
      return true;
    }
    function glide(p) {
      var f0 = Math.floor(p) % N, f1 = (f0 + 1) % N, a = p - Math.floor(p);
      if (base.width !== cv.width || base.height !== cv.height) { base.width = cv.width; base.height = cv.height; baseF = -1; }
      if (baseF !== f0) { paintFrame(baseCtx, f0, true); baseF = f0; }
      ctx.drawImage(base, 0, 0);
      if (a > 0.03) { ctx.globalAlpha = a; paintFrame(ctx, f1, false); ctx.globalAlpha = 1; }
    }
    function tick(now) {
      loopRaf = 0;
      if (!running()) { settle(); return; }
      if (now < holdUntil) { lastT = now; loopRaf = requestAnimationFrame(tick); return; }
      var dt = Math.min(now - lastT, 64);
      lastT = now;
      pos = (pos + dt * N / TURN) % N;
      cur = Math.floor(pos);
      if (now - lastPaint >= 30) { lastPaint = now; glide(pos); }
      loopRaf = requestAnimationFrame(tick);
    }
    function running() { return active && inView && pageOn && !held && !dragging; }
    // Stopped mid-glide, the canvas would hold two frames at once; paint the
    // whole one it was passing and tell a screen reader the angle.
    function settle() { draw(); say(); }
    function sync() {
      showPlay();
      if (!running()) {
        if (loopRaf) { cancelAnimationFrame(loopRaf); loopRaf = 0; settle(); }
        return;
      }
      if (loopRaf) return;
      if (!ready || !allHere()) {
        open();
        if (!waitT) waitT = setTimeout(function () { waitT = 0; sync(); }, 120);
        return;
      }
      pos = cur; lastT = lastPaint = performance.now();
      if (firstRun) { firstRun = false; if (cur === 0) { holdUntil = lastT + HOLD_FIRST; glide(0); } }
      loopRaf = requestAnimationFrame(tick);
    }
    // The shopper took over (touch, drag, keys): stop where it is.
    function hold() { if (!held) { held = true; sync(); } }

    // THE PLAY CONTROL: a bare pause or play glyph in the stage's bottom-left
    // corner, the same quiet weight as the hint mark (no tile, no box: Andrew's
    // rule for product photography), on a 44px tap area. A sibling of the 360
    // in the stage, not inside it, so a press on it never starts a drag and the
    // slider keeps no button inside it. Shown only while the 360 is chosen.
    // 28px glyphs, 2px strokes (22px and 1.6 before, 15 Oct 2026: the audit found the mark too small to find).
    var PAUSE = '<svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M9 6v12M15 6v12"/></svg>';
    var PLAY = '<svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round" aria-hidden="true"><path d="M8 5.5v13l10.5-6.5z"/></svg>';
    var playBtn = document.createElement('button');
    playBtn.type = 'button';
    playBtn.className = 'spin-play';
    playBtn.hidden = true;
    playBtn.setAttribute('aria-controls', el.id || '');
    var shown = null;
    function showPlay() {
      playBtn.hidden = !active;
      if (shown === held) return;
      shown = held;
      playBtn.innerHTML = held ? PLAY : PAUSE;
      playBtn.setAttribute('aria-label', held ? 'Play the turn' : 'Pause the turn');
    }
    playBtn.addEventListener('click', function () {
      held = !held;
      if (!held) open();
      sync();
    });
    if (el.parentNode) el.parentNode.appendChild(playBtn);

    // show() tells the 360 whether it is the chosen view.
    function visible(on) {
      active = !!on;
      if (active) open();
      sync();
    }
    function arrive() { visible(true); }
    if ('IntersectionObserver' in window) {
      var viewIo = new IntersectionObserver(function (entries) {
        var e = entries[entries.length - 1];
        inView = e.isIntersecting && e.intersectionRatio >= 0.2;
        sync();
      }, { threshold: [0, 0.2, 0.5] });
      viewIo.observe(el);
      // A 360 already showing on load (no switch) is chosen once half of it
      // is on screen.
      var seenIo = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var view = el.closest('.stage__view');
          if (entries[i].isIntersecting && (!view || view.hasAttribute('data-active'))) {
            visible(true); seenIo.disconnect(); return;
          }
        }
      }, { threshold: 0.5 });
      seenIo.observe(el);
    }
    document.addEventListener('visibilitychange', function () { pageOn = !document.hidden; sync(); });

    var dragging = false, lastX = 0, acc = 0;
    el.addEventListener('pointerdown', function (e) {
      stopCoast();
      hold();
      dragging = true; lastX = e.clientX; acc = 0;
      samples = []; sample(e.clientX);
      el.classList.add('is-dragging');
      if (el.setPointerCapture) el.setPointerCapture(e.pointerId);
      open();
    });
    el.addEventListener('pointermove', function (e) {
      if (!dragging || !ready) return;
      sample(e.clientX);
      acc += (e.clientX - lastX); lastX = e.clientX;
      while (Math.abs(acc) >= STEP) {
        if (acc > 0) { step(-1); acc -= STEP; } else { step(1); acc += STEP; }
        spun();
      }
    });
    ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (ev) {
      el.addEventListener(ev, function () {
        if (!dragging) return;
        dragging = false; el.classList.remove('is-dragging');
        // pointerleave with the pointer captured only fires on release, so all
        // three ends of a drag arrive here and coast alike.
        if (ev !== 'pointercancel') coast(velocity());
      });
    });
    // Keys (3 Oct 2026, accessibility pass, slider conventions): Left/Down
    // and Right/Up one frame, with Shift or PageUp/PageDown a quarter turn,
    // Home back to the front. Returns true when it used the key, so the 360°
    // switch button can share it.
    function key(e) {
      var Q = Math.max(1, Math.round(N / 4)), k = e.key;
      if (k === 'ArrowLeft' || k === 'ArrowDown') step(e.shiftKey ? -Q : -1);
      else if (k === 'ArrowRight' || k === 'ArrowUp') step(e.shiftKey ? Q : 1);
      else if (k === 'PageDown') step(-Q);
      else if (k === 'PageUp') step(Q);
      else if (k === 'Home') step(-cur);
      else return false;
      open();
      hold();
      spun();
      return true;
    }
    el.addEventListener('keydown', function (e) { if (key(e)) e.preventDefault(); });

    return { open: open, prime: prime, arrive: arrive, visible: visible, key: key };
  }

  function init() {
    // The snippet ships its own <script>, so a page with two stages would run
    // this twice — bind once per element and let the flag make it idempotent.
    document.querySelectorAll('[data-stage-root]').forEach(function (el) {
      if (el.__glStage) return;
      el.__glStage = true;
      Stage(el);
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
